import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../firebase/AuthProvider';
import Ticket from '../../../interfaces/ticket/Ticket';
import Sprint from '../../../interfaces/sprint/Sprint';
import TicketStatus from '../../../interfaces/ticket/TicketStatus';
import { User } from '../../../interfaces/User';
import { getTickets, getAssignableUsers, getTicketTags, reorderTickets } from '../../../services/ticketServices/TicketService';
import { getActiveSprint, getSprints } from '../../../services/sprintServices/SprintService';
import { getStatuses } from '../../../services/statusServices/StatusService';
import Loader from '../loader/Loader';
import { Button, Tab, Tabs } from 'react-bootstrap';
import SprintBoard from '../sprintview/SprintBoard';
import BacklogView from '../sprintview/BacklogView';
import Tag from '../../../interfaces/tag/Tag';
import CreateTicketModal from './CreateTicketModal';
import CreateSprintModal from '../sprintview/CreateSprintModal';
import { createSprint } from '../../../services/sprintServices/SprintService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck, faClipboardList, faFilter } from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from '../../../contexts/NotificationProvider';
import AvatarFilter from '../ui/AvatarFilter';
import '../ui/AvatarFilter.css';
import { ReorderRequest } from '../../../interfaces/ticket/ReorderRequest';
import IssueSidebar from "../issuePage/IssueSidebar";
import IssuesFilter from './IssuesFilter';

const IssuesPageComponent: React.FC = () => {
    const { currentUser } = useAuth();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [sprints, setSprints] = useState<Sprint[]>([]);
    const [activeSprint, setActiveSprint] = useState<Sprint | undefined>();
    const [statuses, setStatuses] = useState<TicketStatus[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [assignableUsers, setAssignableUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCreateSprintModal, setShowCreateSprintModal] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const { notify } = useNotifications();
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

    const [showFilter, setShowFilter] = useState(false);
    const [activeFilters, setActiveFilters] = useState({
        searchTerm: "",
        assignees: [] as string[],
        statuses: [] as string[],
        tags: [] as string[]
    });

    // Keyboard shortcut for creating a ticket
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // 1. Don't trigger if user is typing in an input or textarea
            const activeElement = document.activeElement;
            const isTyping =
                activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement ||
                activeElement?.getAttribute('contenteditable') === 'true';

            if (isTyping) return;

            // 2. Don't trigger if an Offcanvas (Sidebar or Filter) is open
            // We check internal state and the presence of Bootstrap's .show class
            const isOffcanvasOpen = !!selectedTicketId || showFilter || !!document.querySelector('.offcanvas.show');
            if (isOffcanvasOpen) return;

            // 3. Trigger on 'c' key if no modifiers (Ctrl/Cmd) are held
            if (event.key.toLowerCase() === 'c' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                event.preventDefault();
                setShowCreateModal(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedTicketId, showFilter]);

    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const matchesSearch = !activeFilters.searchTerm ||
                ticket.title.toLowerCase().includes(activeFilters.searchTerm.toLowerCase());

            const matchesStatus = activeFilters.statuses.length === 0 ||
                activeFilters.statuses.includes(ticket.status);

            const matchesTags = activeFilters.tags.length === 0 ||
                ticket.tags?.some(tag => activeFilters.tags.includes(tag.name));

            const matchesAssignee = activeFilters.assignees.length === 0 ||
                (ticket.assignee && activeFilters.assignees.includes(ticket.assignee.id));

            return matchesSearch && matchesStatus && matchesTags && matchesAssignee;
        });
    }, [tickets, activeFilters]);

    const handleTicketUpdate = (updatedTicket: Ticket) => {
        setTickets(prevTickets =>
            prevTickets.map(ticket =>
                ticket.id === updatedTicket.id ? updatedTicket : ticket
            )
        );
    };

    const handleCloseSidebar = useCallback(() => {
        setSelectedTicketId(null);
    }, []);

    const activeTab = searchParams.get('tab') || 'backlog';

    // const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
    const handleAvatarSelectionChange = (newUserIds: string[]) => {
        setActiveFilters(prev => ({
            ...prev,
            assignees: newUserIds
        }));
    };

    const fetchData = async (isBackgroundRefresh = false) => {
        if (!currentUser) return;
        if (!isBackgroundRefresh) {
            setIsLoading(true);
        }
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const [ticketData, activeSprint, sprintData, statusData, tagData, userData] = await Promise.all([
                getTickets(token, {}),
                getActiveSprint(token),
                getSprints(token),
                getStatuses(token),
                getTicketTags(token),
                getAssignableUsers(token)
            ]);

            setTickets(ticketData?.tickets || []);
            setActiveSprint(activeSprint || undefined);
            setSprints(sprintData || []);
            setStatuses(statusData || []);
            setTags(tagData || []);
            setAssignableUsers(userData || []);
        } catch (err) {
            setError('Failed to fetch data. Please try again.');
            console.error(err);
        } finally {
            if (!isBackgroundRefresh) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentUser]);

    const handleCreateSprint = async (newSprint: { name: string; startDate: string; endDate: string }) => {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        const createdSprint = await createSprint(token, newSprint);
        if (createdSprint) {
            fetchData();
        } else {
            console.error("Failed to create sprint");
        }
    };

    const handleTicketStatusUpdate = (ticketId: string, newStatus: string) => {
        setTickets(prevTickets =>
            prevTickets.map(ticket =>
                ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
            )
        );
    };

    const handleTicketReorder = async (payload: ReorderRequest) => {
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        const success = await reorderTickets(token, payload);

        if (success) {
            notify('Ticket moved successfully!', { type: 'success' });
            fetchData(true);
        } else {
            notify('Failed to move ticket.', { type: 'error' });
            fetchData(true);
        }
    };

    const handleSelect = (k: string | null) => {
        if (k) {
            setSearchParams({ tab: k });
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="sprint-view-page tasks_page_container shadow-sm p-3">
            <div className="top_bar_flx">
                <div className="top_right flex justify-between">
                    <div className='flex gap-2'>
                        <Button
                            variant="primary"
                            className="btn_sm"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Create Ticket
                        </Button>
                        <AvatarFilter
                            users={assignableUsers}
                            selectedUserIds={activeFilters.assignees}
                            onSelectionChange={handleAvatarSelectionChange}
                        />
                    </div>
                    <Button
                        variant="light"
                        className="btn_sm"
                        onClick={() => setShowFilter(true)}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        Filter
                    </Button>
                </div>
            </div>
            <Tabs activeKey={activeTab} onSelect={handleSelect} id="sprint-view-tabs" className="mb-3 mt-3">
                <Tab eventKey="backlog" title={<><FontAwesomeIcon icon={faListCheck} /> Backlog</>}>
                    <BacklogView
                        tickets={filteredTickets}
                        sprints={sprints}
                        onTicketSprintChange={handleTicketReorder}
                        onTicketClick={setSelectedTicketId}
                    />
                </Tab>
                <Tab eventKey="sprint-board" title={<><FontAwesomeIcon icon={faClipboardList} /> Sprint Board</>}>
                    {activeSprint ? (
                        <SprintBoard
                            tickets={filteredTickets.filter(t => t.sprint?.id === activeSprint?.id)}
                            statuses={statuses}
                            sprint={activeSprint}
                            onTicketStatusChange={handleTicketStatusUpdate}
                            refreshData={fetchData}
                            onTicketClick={setSelectedTicketId}
                        />
                    ) : (
                        <div className="text-center p-5">
                            <h4>There is no active sprint.</h4>
                            <Button variant="primary" onClick={() => setShowCreateSprintModal(true)}>
                                Create New Sprint
                            </Button>
                        </div>
                    )}
                </Tab>
            </Tabs>
            <CreateTicketModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                assignees={assignableUsers}
                sprints={sprints}
                statuses={statuses || []}
                tags={tags}
                onTicketCreated={fetchData}
            />
            <CreateSprintModal
                show={showCreateSprintModal}
                onHide={() => setShowCreateSprintModal(false)}
                onSprintCreate={handleCreateSprint}
            />
            <IssueSidebar
                issueId={selectedTicketId}
                onClose={handleCloseSidebar}
                onTicketUpdate={handleTicketUpdate}
            />
            <IssuesFilter
                show={showFilter}
                onHide={() => setShowFilter(false)}
                tags={tags}
                statuses={statuses}
                sprints={sprints}
                assignableUsers={assignableUsers}
                onApplyFilters={setActiveFilters}
            />
        </div>
    );
};

export default IssuesPageComponent;